import React, { ChangeEvent, useState } from 'react';
import { Input, Image, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
// Types
import { BannersOption } from 'pages/HomePageManagement';
import { ToolbarItem } from 'components/common/RichText/components/Toolbar/Toolbar';
// Ui
import VideoPreview from 'ui/VideoPreview/VideoPreview';
// Components
import RichText from 'components/common/RichText/RichText';
import UploadImageModal from 'pages/HomePageManagement/components/UploadImageModal/UploadImageModal';
import UploadVideoModal from 'pages/HomePageManagement/components/UploadVideoModal/UploadVideoModal';
// Styles
import './styles.scss';

// Note: this component can't be covered by unit tests for now because
// the issue caused by the Editable component from the 'slate-react' package
export type SetupBannersItemProps = {
  onBannerChange: (
    index: number,
    data: string | boolean,
    field: string,
    imageId?: string
  ) => void;
  item: BannersOption;
  index: number;
};

const SetupBannersItem: React.FC<SetupBannersItemProps> = ({
  onBannerChange,
  item,
  index,
}) => {
  const [isUploadImageVisible, setUploadImageVisible] =
    useState<boolean>(false);
  const [isVideoUploadVisible, setVideoUploadVisible] =
    useState<boolean>(false);

  const handleImageChange = (url: string, id: string) => {
    onBannerChange(index, url, 'image', id);
  };

  const handleVideoChange = (url: string, id: string) => {
    onBannerChange(index, url, 'video', id);
  };

  const handleBannerTitleChange = (value: string): void => {
    onBannerChange(index, value, 'title');
  };

  const handleBannerInputChange = ({
    target: { value, name },
  }: ChangeEvent<HTMLInputElement>) => {
    onBannerChange(index, value, name);
  };

  const handleBannerCheckboxChange = ({
    target: { name, checked },
  }: ChangeEvent<HTMLInputElement>) => {
    onBannerChange(index, checked, name);
  };

  const descriptionId = `banner-${index}-description`;
  const buttonContentId = `banner-${index}-button-content`;
  const linkId = `banner-${index}-link`;
  const shouldLinkRedirectId = `banner-${index}-should-link-redirect`;

  const toggleUploadModal = () => {
    setUploadImageVisible((prev) => !prev);
  };

  const toggleVideoUploadModal = () => {
    setVideoUploadVisible((prev) => !prev);
  };

  return (
    <>
      <div className="setup-banners-item">
        <div className="setup-banners-item-media">
          <div className="setup-banners-item-field">
            <span className="setup-banners-item-label">Banner image</span>
            {item?.image ? (
              <Image
                width={112}
                height={112}
                src={item.image}
                preview={false}
                onClick={toggleUploadModal}
                className="setup-banners-item-image"
              />
            ) : (
              <Button
                className="setup-banners-item-picker"
                type="dashed"
                size="large"
                onClick={toggleUploadModal}
              >
                <PlusOutlined />
                <span>Choose image</span>
              </Button>
            )}
          </div>

          <div className="setup-banners-item-field">
            <span className="setup-banners-item-label">Banner video</span>
            {item?.video ? (
              <button
                onClick={toggleVideoUploadModal}
                className="setup-banners-item-video"
              >
                <VideoPreview video={item.video} show noControl />
              </button>
            ) : (
              <Button
                className="setup-banners-item-picker"
                type="dashed"
                size="large"
                onClick={toggleVideoUploadModal}
              >
                <PlusOutlined />
                <span>Choose video</span>
              </Button>
            )}
          </div>
        </div>

        <div className="setup-banners-item-field">
          <span className="setup-banners-item-label">Title</span>
          <div className="setup-banners-item-richtext-wrapper">
            <RichText
              onChange={handleBannerTitleChange}
              placeholder="Type title"
              initialValue={item.title || ''}
              toolbarItems={[ToolbarItem.Mark]}
            />
          </div>
        </div>

        <div className="setup-banners-item-field">
          <label className="setup-banners-item-label" htmlFor={descriptionId}>
            Description
          </label>
          <Input
            id={descriptionId}
            name="description"
            value={item.description || ''}
            onChange={handleBannerInputChange}
            placeholder="Type description"
          />
        </div>

        <div className="setup-banners-item-field">
          <label className="setup-banners-item-label" htmlFor={buttonContentId}>
            Button label
          </label>
          <Input
            id={buttonContentId}
            name="buttonContent"
            value={item.buttonContent || ''}
            onChange={handleBannerInputChange}
            placeholder="Type content"
          />
        </div>

        <div className="setup-banners-item-field">
          <label className="setup-banners-item-label" htmlFor={linkId}>
            Button link
          </label>
          <Input
            id={linkId}
            name="link"
            value={item.link || ''}
            onChange={handleBannerInputChange}
            placeholder="Type link"
          />
        </div>

        <div className="setup-banners-item-checkbox">
          <Input
            className="setup-banners-item-checkbox-input"
            id={shouldLinkRedirectId}
            name="shouldLinkRedirect"
            type="checkbox"
            checked={item.shouldLinkRedirect || false}
            onChange={handleBannerCheckboxChange}
          />
          <label htmlFor={shouldLinkRedirectId}>Should link redirect</label>
        </div>
      </div>

      {isUploadImageVisible && (
        <UploadImageModal
          isVisible={isUploadImageVisible}
          onCancel={toggleUploadModal}
          title="Upload Banner Image"
          onImageChange={handleImageChange}
        />
      )}
      {isVideoUploadVisible && (
        <UploadVideoModal
          isVisible={isVideoUploadVisible}
          onCancel={toggleVideoUploadModal}
          title="Upload Banner Video"
          onVideoChange={handleVideoChange}
        />
      )}
    </>
  );
};

export default SetupBannersItem;
